import type { TFunction } from 'i18next'

import { Environment, getCountryCodeBookId, getDefaultAppLanguage, Market } from '@leanpay/utils'

// START COMMON CONSTANTS
const getMarketFromHostname = (): Market => {
    const hostname = window.location.hostname

    if (hostname.includes('.leanpay.ro') || hostname.includes('rou')) {
        return Market.ROM
    }

    if (hostname.includes('.leanpay.si') || hostname.includes('slo')) {
        return Market.SLO
    }

    if (hostname.includes('.leanpay.hu') || hostname.includes('hun')) {
        return Market.HUN
    }

    throw new Error(`[MARKET] Unknown hostname: ${hostname}`)
}

const isPrivateIp =
    /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(
        window.location.hostname,
    )
const isRunningOnLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    isPrivateIp ||
    (window.location.hostname.includes('leanpay') && !window.location.hostname.includes('.'))

export const currentMarket = isRunningOnLocalhost
    ? (import.meta.env.VITE_MARKET as Market)
    : getMarketFromHostname()

export const isMarketSlo = currentMarket === Market.SLO
export const isMarketRom = currentMarket === Market.ROM

const getEnvironmentFromHostname = () => {
    const hostname = window.location.hostname

    if (isRunningOnLocalhost) {
        return Environment.LOCAL
    }

    if (hostname.includes('dev')) {
        return Environment.TILT
    }

    if (hostname.includes('test-pre-qualify')) {
        return Environment.TEST
    }
    if (hostname.includes('stage-pre-qualify')) {
        return Environment.STAGE
    }

    if (hostname.includes('pre-qualify')) {
        return Environment.PROD
    }

    throw new Error(`[ENV] Unknown hostname: ${hostname}`)
}

export const currentEnvironment = getEnvironmentFromHostname()

export const isLocalEnvironment = currentEnvironment === Environment.LOCAL
export const isTestEnvironment = currentEnvironment === Environment.TEST
export const isStageEnvironment = currentEnvironment === Environment.STAGE
export const isProdEnvironment = currentEnvironment === Environment.PROD
export const isNonProdLiveEnvironment = !isProdEnvironment && !isLocalEnvironment

export const defaultAppLanguage = getDefaultAppLanguage(currentMarket)
export const countryCodebookId = getCountryCodeBookId(currentMarket)

const getGtmId = () => {
    if (isProdEnvironment) {
        if (isMarketRom) {
            return 'GTM-NS79LZF'
        }
        if (isMarketSlo) {
            return 'GTM-M3G2LTW'
        }
    }

    if (isStageEnvironment) {
        if (isMarketRom) {
            return 'GTM-P563JMZW'
        }
        if (isMarketSlo) {
            return 'GTM-WHBP5PGR'
        }
    }

    return ''
}

export const gtmId = getGtmId()

// END COMMON CONSTANTS

export const taxNumberValidationRegex = new RegExp(/^[0-9]{11}$/)

export enum PreQualifiedRoute {
    PrequalifiedIntro = 'check-limit',
    RequestOtp = 'request-otp',
    Login = 'login',
    PersonalDetails = 'personal-details',
    TwoFaPin = 'two-fa-pin',
    FinancialDetails = 'financial-details',
    PreviewFinancialDetails = 'preview-financial-details',
    PrequalifiedSuccess = 'prequalified-success',
    PrequalifiedError = 'prequalified-error',
    UpdateUserInfo = 'update-user-info',
    AddressDetails = 'address-details',
    ValidateOffer = 'validate-offer',
}

export enum CacheKey {
    Companies = 'Companies',
    EmployerNames = 'EmployerNames',
    StateMachineStatus = 'StateMachineStatus',
    KYCStatus = 'KYCStatus',
    Settlement = 'Settlement',
    PersonalDetails = 'PersonalDetails',
    LoadedPersonalDetails = 'LoadedPersonalDetails',
    RequestPIN = 'RequestPIN',
    RequestOTPOverInvoice = 'RequestOTPOverInvoice',
    Authentication = 'Authentication',
    VendorToken = 'VendorToken',
    FinancialDetailsCheck = 'FinancialDetailsCheck',
    SessionInfo = 'SessionInfo',
    PrequalifiedInfo = 'PrequalifiedInfo',
    SystemConfig = 'SystemConfig',
    AddressDetails = 'AddressDetails',
    FinancialDetails = 'FinancialDetails',
}

export const romFaqQuestions = (translate: TFunction, params: any) => {
    return [
        {
            question: translate('preQualifySuccess.faqQuestion1', {
                amountAndCurrency: params,
            }),
            answer: translate('preQualifySuccess.faqAnswer1', {
                amountAndCurrency: params,
            }),
        },
        {
            question: translate('preQualifySuccess.faqQuestion2', {
                amountAndCurrency: params,
            }),
            answer: translate('preQualifySuccess.faqAnswer2', {
                amountAndCurrency: params,
            }),
        },
        {
            question: translate('preQualifySuccess.faqQuestion3'),
            answer: translate('preQualifySuccess.faqAnswer3'),
        },
        {
            question: translate('preQualifySuccess.faqQuestion4'),
            answer: translate('preQualifySuccess.faqAnswer4'),
        },
    ]
}
